import React, { useState, useRef, useCallback, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  PhotoIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/solid";
import { useDropzone } from "react-dropzone";
import UploadGuideDialog from "./UploadGuideDialog";

interface NewProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject: (name: string, images: File[]) => void;
}

export default function NewProjectDialog({
  isOpen,
  onClose,
  onCreateProject,
}: NewProjectDialogProps) {
  const [projectName, setProjectName] = useState("Untitled Project");
  const [projectImages, setProjectImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [fileRejections, setFileRejections] = useState<string | null>(null);
  const [showUploadGuide, setShowUploadGuide] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if upload guide should be shown
  useEffect(() => {
    if (isOpen) {
      // Check localStorage to see if user chose not to show guide again
      const hideUploadGuideTutorial = localStorage.getItem(
        "hideUploadGuideTutorial"
      );
      if (hideUploadGuideTutorial !== "true") {
        setShowUploadGuide(true);
      }
    }
  }, [isOpen]);

  // Process files whether from input or dropzone
  const processFiles = useCallback((files: File[]) => {
    // Filter to only allow PNG or JPEG files and take only the first one
    const validFiles = files.filter(
      (file) => file.type === "image/png" || file.type === "image/jpeg"
    );

    if (validFiles.length === 0) {
      setFileRejections("Please upload a PNG or JPEG image file.");
      return;
    }

    // Take only the first file
    const fileToUse = [validFiles[0]];
    setProjectImages(fileToUse);
    setFileRejections(null);

    // Create preview URL for the selected image
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrls([reader.result as string]);
    };
    reader.readAsDataURL(fileToUse[0]);
  }, []);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      processFiles(files);
    }
  };

  // Setup dropzone
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        setFileRejections("Please upload a PNG or JPEG image file.");
        return;
      }

      processFiles(acceptedFiles);
    },
    [processFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    maxFiles: 1,
  });

  const handleSubmit = () => {
    console.log("projectName", projectName);
    console.log("projectImages", projectImages);
    if (projectName.trim() && projectImages.length > 0) {
      onCreateProject(projectName, projectImages);
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setProjectName("");
    setProjectImages([]);
    setPreviewUrls([]);
    setFileRejections(null);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <UploadGuideDialog
        isOpen={showUploadGuide}
        onClose={() => setShowUploadGuide(false)}
      />

      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        {/* Backdrop with blur effect */}
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-all duration-300"
          aria-hidden="true"
        />

        {/* Full-screen container for centering with height constraint and overflow handling */}
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-xl transition-all duration-300 transform my-8 max-h-[90vh] flex flex-col">
            {/* Close button */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 transition-colors duration-200 focus:outline-none hover:cursor-pointer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Dialog header - fixed at top */}
            <div className="pt-6 px-6 flex-shrink-0">
              <Dialog.Title className="text-2xl font-bold text-center mb-2 text-gray-900 dark:text-white transition-colors duration-200">
                Upload Product Image
              </Dialog.Title>
              <Dialog.Description className="text-md text-gray-600 dark:text-gray-300 text-center mb-6 transition-colors duration-200">
                We'll use this to generate your UGC
              </Dialog.Description>
            </div>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-6 pb-3">
              {/* Project Name Input */}
              <div className="mb-6">
                <label
                  htmlFor="project-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  Project Name
                </label>
                <input
                  type="text"
                  id="project-name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-800 dark:text-white transition-all duration-200 outline-none"
                  placeholder="Enter project name"
                />
              </div>

              {/* Image upload area */}
              <div className="mb-6">
                <div
                  {...getRootProps()}
                  className={`w-full h-48 rounded-xl border-2 border-dashed ${
                    isDragActive
                      ? "border-purple-500 bg-purple-50/70 dark:bg-purple-900/30"
                      : "border-purple-400/50 dark:border-purple-500/50 bg-purple-50/50 dark:bg-purple-900/10"
                  } flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/70 dark:hover:border-purple-400/70 hover:bg-purple-50/80 dark:hover:bg-purple-900/20 transition-all duration-300`}
                >
                  {previewUrls.length > 0 ? (
                    <div className="w-full h-full p-3 overflow-auto">
                      <img
                        src={previewUrls[0]}
                        alt="Product"
                        className="w-full h-full object-contain rounded-lg shadow-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <input {...getInputProps()} />
                      <PhotoIcon className="h-10 w-10 text-purple-400 dark:text-purple-500 mb-2" />
                      <div>
                        <span className="text-purple-600 dark:text-purple-400 font-medium">
                          Click to upload
                        </span>
                        <span className="text-gray-600 dark:text-gray-300">
                          {" "}
                          or drag and drop
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        PNG, JPG, JPEG • Max. 1MB
                      </div>
                      {isDragActive && (
                        <div className="mt-2 text-sm text-purple-600 dark:text-purple-400 font-medium">
                          Drop the file here...
                        </div>
                      )}
                    </>
                  )}
                </div>
                {fileRejections && (
                  <div className="mt-2 text-sm text-red-500 dark:text-red-400">
                    {fileRejections}
                  </div>
                )}
              </div>

              {projectImages.length > 0 && (
                <div className="mb-4 text-sm flex items-center text-gray-600 dark:text-gray-300">
                  <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full px-2 py-0.5 text-xs font-medium mr-2">
                    {projectImages.length} image selected
                  </div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    Image size: {Math.round(projectImages[0].size / 1024)} KB
                  </span>
                </div>
              )}

              {/* Guide Button */}
              <div className="mb-6">
                <button
                  onClick={() => setShowUploadGuide(true)}
                  className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 hover:cursor-pointer"
                >
                  <InformationCircleIcon className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-2" />
                  <span className="text-sm font-medium">View Upload Guide</span>
                </button>
              </div>
            </div>

            {/* Footer - fixed at bottom */}
            <div className="bg-gray-50 dark:bg-gray-800/50 py-4 px-6 flex justify-between items-center transition-colors duration-200 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 rounded-b-2xl ">
              <button
                onClick={() => setShowUploadGuide(true)}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200 flex items-center hover:cursor-pointer"
              >
                <QuestionMarkCircleIcon className="h-4 w-4 mr-1.5" />
                Need Help?
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 text-sm font-medium hover:cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={`px-5 py-2.5 rounded-lg text-white text-sm font-medium shadow-sm transition-all duration-200 flex items-center ${
                    !projectName.trim() || projectImages.length === 0
                      ? "bg-purple-400 dark:bg-purple-500/50 cursor-not-allowed opacity-70"
                      : "bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 hover:cursor-pointer"
                  }`}
                  disabled={!projectName.trim() || projectImages.length === 0}
                >
                  Create Project
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
